// ICS 204 Assignment List
import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { CollapsibleCard } from 'components/DesignSystems/Collapse/CollapsibleCard';
import ICSWidget from '../ICSWidget';
import FilterHeader from 'components/DesignSystems/FilterHeader/FilterHeader';
import { getIncidentCardTitle } from '../../USCGPage';
import AssignmentContainer from './AssignmentContainer';
import {
  useWorkAssignments,
  ResourceNeeds,
} from 'components/IAP/hooks/workAssignmentHooks';
import {
  PlannedResource,
  useWorkAssignmentListAttachments,
} from 'components/IAP/hooks/assignmentListHooks';
import { useIncidentPermissions } from 'components/IAP/hooks/incidentHooks';
import dayjs from 'dayjs';

export type WorkGroupResourceNeed = ResourceNeeds & {
  workAssignmentId: string;
  workAssignmentName: string;
};

export type Status =
  | 'awaiting_review'
  | 'draft'
  | 'in_progress'
  | 'completed'
  | 'incomplete';

export type ProposedStatusOption =
  | null
  | 'Draft'
  | 'Assigned'
  | 'Submitted Complete for Review'
  | 'Reported Incomplete for Review'
  | 'Completed'
  | 'Incomplete';

export type SimplifiedWorkAssignment = {
  id: string;
  type: string;
  name: string;
  description: string;
  workGroupName?: string;
  workGroupId?: string;
  overheadPosition?: string;
  specialInstructions?: string;
  reportingLocation?: string;
  timestamp?: string;
};

type AssignmentListItem = {
  walaId: string | null;
  workGroupId: string;
  workGroup: string;
  branch: string;
  division: string;
  walaGroup: string;
  stagingArea: string;
  proposedStatus: ProposedStatusOption;
  actualStatus: string | null;
  archived: boolean;
  sectionChief?: string;
  branchDirector?: string;
  divisionGroupSupervisor?: string;
  workAssignments: SimplifiedWorkAssignment[];
  workGroupResourceNeeds: WorkGroupResourceNeed[];
  plannedResources: PlannedResource[] | null;
  medicalCommunication?: string;
  evacuationCommunication?: string;
  otherCommunication?: string;
  specialInstructions?: string;
};

type AssignmentListColumnKey = Exclude<
  keyof AssignmentListItem,
  | 'id'
  | 'archived'
  | 'workGroupId'
  | 'walaId'
  | 'actualStatus'
  | 'sectionChief'
  | 'branchDirector'
  | 'divisionGroupSupervisor'
  | 'workAssignments'
>;

type AssignmentListProps = {
  periodNumber: number;
};

export const mapProposedStatus = (
  proposedStatus: Status | null,
  actualStatus: Status | null
): ProposedStatusOption => {
  switch (proposedStatus) {
    case 'draft':
      return 'Draft';
    case 'in_progress':
      return 'Assigned';
    case 'completed':
      if (actualStatus === 'awaiting_review') {
        return 'Submitted Complete for Review';
      }
      return 'Completed';
    case 'incomplete':
      if (actualStatus === 'awaiting_review') {
        return 'Reported Incomplete for Review';
      }
      return 'Incomplete';
    default:
      return null; // Default to null if unknown
  }
};

const AssignmentList: React.FC<AssignmentListProps> = ({ periodNumber }) => {
  const [searchParams] = useSearchParams();
  const phase = searchParams.get('phase');
  const mode = searchParams.get('mode');
  const sectionKey = `${phase}-${mode}-assignment-list`;

  const { data: incidentPermissions } = useIncidentPermissions();
  const canEditCards = incidentPermissions?.['incident:editCards'] ?? false;

  const { data: workAssignments } = useWorkAssignments();
  const { data: workAssignmentListAttachments } =
    useWorkAssignmentListAttachments();

  // Todo (aeortic): We can move this to a hook, and thereby evaluate if we can move it all the way to the backend.
  const assignmentListFromWorkAssignments: AssignmentListItem[] =
    useMemo(() => {
      if (!workAssignments || workAssignments.length === 0) return [];

      const listMap = new Map<string, AssignmentListItem>();

      for (const workAssignment of workAssignments) {
        // filter out those work assignments that don't belong to the current operational period
        if (
          periodNumber &&
          workAssignment.operational_period_number !== periodNumber
        ) {
          continue;
        }

        const workGroupId = workAssignment.work_group_id;
        if (!workGroupId) continue;

        const existing = listMap.get(workGroupId);

        const timestamp = workAssignment.timestamp
          ? dayjs(workAssignment.timestamp).toISOString()
          : '';

        const wa = {
          id: workAssignment.id,
          type: '',
          name: workAssignment.work_assignment ?? '',
          description: '',
          workGroupName: workAssignment.work_group_name ?? '',
          workGroupId,
          overheadPosition: (workAssignment as any).overhead_positions ?? '',
          specialInstructions:
            (workAssignment as any).special_instructions ?? '',
          reportingLocation: (workAssignment as any).reporting_location ?? '',
          timestamp,
        };

        const additionalWorkAssignmentResourceNeeds =
          workAssignment.resource_list?.map((resource) => ({
            ...resource,
            workAssignmentId: workAssignment.id,
            workAssignmentName: workAssignment.work_assignment ?? '',
          })) ?? [];

        if (existing) {
          existing.workAssignments.push(wa);
          existing.workGroupResourceNeeds.push(
            ...additionalWorkAssignmentResourceNeeds
          );
          continue; // We've already processed this work group, so we can skip to the next iteration
        }

        const listInCorrectPeriod = workAssignmentListAttachments?.filter(
          (attachment) => {
            return attachment.operationalPeriodNumber === periodNumber;
          }
        );

        // oof, we should try to move all of this string resolution code to the back-end
        const attachment = listInCorrectPeriod?.find(
          (attachment) => attachment.workGroupId === workGroupId
        ) ?? {
          walaId: null,
          workGroupId: workGroupId,
          workGroupName: workAssignment.work_group_name ?? '',
          branch: workAssignment.branch ?? '',
          division: workAssignment.division ?? '',
          walaGroup: '',
          stagingArea: '',
          status: null,
          sectionChief: '',
          branchDirector: '',
          divisionGroupSupervisor: '',
          proposedStatus: null,
          resources: null,
          plannedResources: null,
          medicalCommunication: '',
          evacuationCommunication: '',
          otherCommunication: '',
          specialInstructions: '',
        };

        listMap.set(workGroupId, {
          walaId: attachment.walaId,
          workGroupId: workGroupId,
          workGroup: attachment.workGroupName,
          branch: attachment.branch,
          division: attachment.division,
          walaGroup: attachment.walaGroup,
          stagingArea: attachment.stagingArea,
          proposedStatus: mapProposedStatus(
            attachment.proposedStatus,
            attachment.status
          ),
          actualStatus: attachment.status,
          archived: Boolean((workAssignment as any).archived ?? false),
          sectionChief: (workAssignment as any).section_chief ?? undefined,
          branchDirector: attachment.branchDirector,
          divisionGroupSupervisor: attachment.divisionGroupSupervisor,
          workAssignments: [wa],
          workGroupResourceNeeds: [...additionalWorkAssignmentResourceNeeds],
          plannedResources: attachment.plannedResources,
          medicalCommunication: attachment.medicalCommunication,
          evacuationCommunication: attachment.evacuationCommunication,
          otherCommunication: attachment.otherCommunication,
          specialInstructions: attachment.specialInstructions,
        });
      }

      return Array.from(listMap.values());
    }, [workAssignments, workAssignmentListAttachments, periodNumber]);

  const columns = useMemo<
    Array<{ key: AssignmentListColumnKey; label: string }>
  >(
    () => [
      {
        key: 'workGroup',
        label: 'PRATUS Work Group',
      },
      {
        key: 'branch',
        label: 'Branch',
      },
      {
        key: 'division',
        label: 'Division',
      },
      {
        key: 'walaGroup',
        label: 'Group',
      },
      {
        key: 'stagingArea',
        label: 'Staging Area',
      },
      {
        key: 'proposedStatus',
        label: 'Status',
      },
    ],
    []
  );

  const columnsForFilterHeader = useMemo(() => {
    return columns.map(({ key, label }) => ({
      key,
      label,
      filterOptions: [
        'All',
        'Undefined',
        ...Array.from(
          new Set(
            assignmentListFromWorkAssignments
              .filter((item) => !item.archived)
              .map((item) => item[key])
              .filter(
                (v): v is string => typeof v === 'string' && v.trim().length > 0
              )
          )
        ),
      ],
    }));
  }, [columns, assignmentListFromWorkAssignments]);

  const defaultFilters = useMemo(() => {
    return Object.fromEntries(columns.map((column) => [column.key, 'All']));
  }, [columns]);

  const [selectedFilters, setSelectedFilters] = useState(defaultFilters);

  const filteredList = useMemo(() => {
    return assignmentListFromWorkAssignments.filter((item) => {
      return columns.every(({ key }) => {
        const filterValue = selectedFilters[key];
        if (!filterValue || filterValue === 'All') {
          return true;
        }
        if (filterValue === 'Undefined') {
          return (
            item[key] === undefined ||
            item[key] === null ||
            (typeof item[key] === 'string' && item[key].trim().length === 0)
          );
        }
        return item[key] === filterValue;
      });
    });
  }, [assignmentListFromWorkAssignments, columns, selectedFilters]);

  const handleFilterChange = (columnKey: string, value: string) => {
    // Implement filter change logic here
    setSelectedFilters((prev) => ({
      ...prev,
      [columnKey]: value,
    }));
  };

  const clearFilters = () => {
    // Implement clear filters logic here
    setSelectedFilters(defaultFilters);
  };

  return (
    <>
      <div className="alert alert-warning my-3">
        <strong>Warning:</strong> This feature is a work in progress and is not
        fully functional.
      </div>

      <CollapsibleCard
        sectionKey={sectionKey}
        className="planning-widget"
        contentClassName="content mt-2"
        headerLeft={
          <ICSWidget.HeaderLeft
            iconName="assignment"
            iconColor="primary"
            title={getIncidentCardTitle('assignmentList')}
            subtitle=""
          />
        }
        headerRight={
          <ICSWidget.HeaderRight
            icsRoles={['RESL', 'PSC', 'OSC', 'SOFR', 'COML']}
          />
        }
      >
        <FilterHeader
          filters={selectedFilters}
          onFilterChange={handleFilterChange}
          clearFilters={clearFilters}
          columns={columnsForFilterHeader}
        />
        {assignmentListFromWorkAssignments &&
        filteredList.filter((item) => !item.archived).length > 0 ? (
          filteredList.map((item) => (
            <AssignmentContainer
              key={item.workGroupId}
              item={item}
              columns={columns.map(({ key }) => key)}
              periodNumber={periodNumber}
              readOnly={!canEditCards}
            />
          ))
        ) : (
          <div className="section-main">
            No work assignments defined <br />
            <br />
            Add work assignments to organize operational activities and
            resources
          </div>
        )}
      </CollapsibleCard>
    </>
  );
};

export default AssignmentList;
